/**
 * L'azienda di piattaforma (il «master»), e cosa può farci chi ci si trova dentro.
 *
 * ## Perché è di sola lettura
 *
 * Il master non è un'azienda: è **il modello da cui nascono le altre**. I ruoli che vivono lì
 * sono condivisi con tutte, e le categorie marcate `CopyInNewTenants` vengono copiate in **ogni**
 * azienda nuova. Chi scrive lì non sta modificando i propri dati: sta modificando quelli di tutte
 * le aziende che verranno.
 *
 * Per questo il BE lo ha reso di sola lettura per chi non è super-admin: `POST`/`PATCH`/`DELETE`
 * rispondono `403 MASTER_TENANT_READ_ONLY`.
 *
 * ## ⚠️ Questo modulo non garantisce niente
 *
 * Il muro è del server. Quello che c'è qui serve a **non portare l'utente su schermate dove ogni
 * gesto finisce in un errore**: spegnere i pulsanti col motivo scritto, invece di lasciarglieli
 * premere per scoprire il divieto dopo. Se un giorno il BE cambiasse regola, qui si spegnerebbe
 * qualcosa di permesso — fastidioso, non pericoloso. Il contrario (fidarsi di questo file per la
 * sicurezza) sarebbe l'errore vero.
 *
 * ## ⚠️ Due rifiuti diversi sulla stessa azione
 *
 * Chi **non** ha il claim CRUD prende `401` e al muro non arriva nemmeno. Il `403` lo vede solo
 * chi *avrebbe* il permesso di scrivere. Sono due frasi diverse e vanno dette **entrambe**, non
 * una al posto dell'altra: «non puoi fare questa cosa» e «non puoi farla *qui*» si risolvono in
 * modi opposti — la prima chiedendo un permesso, la seconda cambiando azienda.
 */
import { useMemo } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';

/**
 * L'id del master nel seed della piattaforma.
 *
 * È il **ripiego**, non la fonte: la fonte è il claim `masterTenantId` che il BE mette nel JWT
 * (`JwtSignInManager`, che espone la costante `TenantProvider.MasterTenantFallback`). Sta qui
 * perché un token vecchio, emesso prima che quel claim esistesse, non deve far sparire il
 * riconoscimento — e perché è una regola di prodotto, non una configurazione d'ambiente.
 */
export const MASTER_TENANT_SEED_ID = '00000000-0000-0000-0000-000000000001';

/** Confronto fra id di tenant: il BE li scrive maiuscoli in certi punti e minuscoli in altri. */
function stessoTenant(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * L'azienda su cui si sta **operando** è quella di piattaforma?
 *
 * @param tenantCorrente il tenant attivo: l'override cross-tenant se c'è, altrimenti quello del JWT
 * @param masterDalToken il claim `masterTenantId`, quando il token ce l'ha
 */
export function isMasterTenant(
  tenantCorrente?: string | null,
  masterDalToken?: string | null,
): boolean {
  return stessoTenant(tenantCorrente, masterDalToken ?? MASTER_TENANT_SEED_ID);
}

export interface StatoMasterTenant {
  /** Si sta operando sull'azienda di piattaforma. */
  isMaster: boolean;
  /** Si è sul master **e** non si è super-admin: ogni scrittura tornerà `403`. */
  isReadOnly: boolean;
  /** Il motivo, già scritto, da passare a `<MaeGate mode="disable" reason={…}>`. */
  reason: string | null;
}

/** Il motivo, uno solo e in un posto solo: la stessa frase del catalogo errori. */
export const MASTER_TENANT_REASON =
  'Questa è l’azienda modello: quello che c’è qui viene copiato in ogni azienda nuova, quindi si modifica solo da super-amministratore. Cambia azienda dal menu in alto per lavorare sui tuoi dati.';

/**
 * La decisione, senza React attorno: è la parte che vale la pena testare.
 *
 * ⚠️ `tenantCorrente` deve essere l'**override cross-tenant** quando c'è, e solo in sua assenza
 * il tenant del token: chi amministra un sottotenant *dal* master sta scrivendo là, non qui, e
 * spegnergli i pulsanti sarebbe sbagliato quanto lasciarli accesi nel caso opposto.
 */
export function decidiMasterTenant(input: {
  tenantCorrente?: string | null;
  masterDalToken?: string | null;
  isSuperAdmin?: boolean;
}): StatoMasterTenant {
  const isMaster = isMasterTenant(input.tenantCorrente, input.masterDalToken);
  const isReadOnly = isMaster && !input.isSuperAdmin;
  return { isMaster, isReadOnly, reason: isReadOnly ? MASTER_TENANT_REASON : null };
}

/**
 * L'id del tenant di piattaforma secondo **questo** token, o il ripiego del seed.
 *
 * ⚠️ Serve per rispondere a una domanda diversa da quella di `useMasterTenant`, che chiede «sto
 * operando sul master?». Qui la domanda è «**quest'altro** tenant è il master?», e capita quando
 * si guarda la madre di un'azienda: la capogruppo di un cliente è figlia della piattaforma, e
 * quel legame non si mostra. Leggere il claim a mano nei punti chiamanti vorrebbe dire
 * riscrivere ogni volta il ripiego sul seed, che è la parte che si dimentica.
 */
export function useMasterTenantId(): string | null {
  const { decoded } = useAuth();
  return typeof decoded?.masterTenantId === 'string' ? decoded.masterTenantId : MASTER_TENANT_SEED_ID;
}

/** Lo stato per la schermata corrente. La regola sta in `decidiMasterTenant`. */
export function useMasterTenant(): StatoMasterTenant {
  const { decoded } = useAuth();

  return useMemo(
    () =>
      decidiMasterTenant({
        tenantCorrente: decoded?.tenantId ?? null,
        masterDalToken: typeof decoded?.masterTenantId === 'string' ? decoded.masterTenantId : null,
        isSuperAdmin: decoded?.superadmin === 'True',
      }),
    [decoded],
  );
}
